<template>
  <div class="success-wrapper">
    <div class="success-container">
      <!-- Success Icon -->
      <div class="success-icon">
        <el-icon :size="80" color="#67c23a">
          <SuccessFilled />
        </el-icon>
      </div>

      <!-- Success Message -->
      <h1 class="success-title">{{ t('success.title') }}</h1>
      <p class="success-message">{{ t('success.message') }}</p>

      <!-- Reservation Info -->
      <div class="reservation-info" v-if="reservationData">
        <div class="info-item">
          <span class="info-label">{{ t('success.reservationId') }}:</span>
          <span class="info-value">{{ reservationData.reservationId }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{{ t('success.phone') }}:</span>
          <span class="info-value">{{ reservationData.phone }}</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <el-button type="primary" size="large" @click="goToMyReservations">
          {{ t('success.viewMyReservations') }}
        </el-button>
        <el-button size="large" @click="goToHome">
          {{ t('success.backToHome') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { SuccessFilled } from '@element-plus/icons-vue'

const { t } = useI18n()

interface ReservationData {
  reservationId: number
  phone: string
  createTime: string
}

const reservationData = ref<ReservationData | null>(null)

onMounted(() => {
  // Get reservation data from URL hash
  const hash = window.location.hash
  const match = hash.match(/#\/success\/(.+)/)
  if (match && match[1]) {
    try {
      reservationData.value = JSON.parse(decodeURIComponent(match[1]))
    } catch (e) {
      console.error('Failed to parse reservation data:', e)
    }
  }
})

const goToMyReservations = () => {
  if (reservationData.value?.phone) {
    window.location.href = `/#/my-reservations?phone=${reservationData.value.phone}`
  }
}

const goToHome = () => {
  window.location.href = '/'
}
</script>

<style scoped>
.success-wrapper {
  min-height: 100vh;
  background: #f0ebf8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.success-container {
  max-width: 600px;
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 60px 40px;
  text-align: center;
}

.success-icon {
  margin-bottom: 24px;
}

.success-title {
  font-size: 32px;
  font-weight: 600;
  color: #67c23a;
  margin: 0 0 16px 0;
}

.success-message {
  font-size: 16px;
  color: #606266;
  margin: 0 0 40px 0;
  line-height: 1.6;
}

.reservation-info {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
  text-align: left;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e4e7ed;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #606266;
}

.info-value {
  color: #303133;
}

.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.action-buttons .el-button {
  min-width: 160px;
}

@media (max-width: 768px) {
  .success-container {
    padding: 40px 24px;
  }

  .success-title {
    font-size: 24px;
  }

  .success-message {
    font-size: 14px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .el-button {
    width: 100%;
  }
}
</style>
